/**
 * `Ruler` — the one-drop bar ruler. `docs/design-direction.md` §6.6.
 *
 * One motif, five jobs, and it is what makes the system read as authored rather than
 * assembled: a row of 1px ticks in `--border-strong` where **every third tick** is
 * full height and printed in `--accent`. That third beat is the one drop.
 *
 * | variant  | job (§6.6)                                                        |
 * |----------|-------------------------------------------------------------------|
 * | `scale`  | the tick scale above the Now Playing waveform, and the no-peaks    |
 * |          | fallback ruler of §6.7                                            |
 * | `glyph`  | the 3-tick bullet in a list or a section rule                      |
 * | `nav`    | the active mark in the bottom nav strip (§9.2)                     |
 * | `loader` | the loading indicator — a single `--accent` tick advancing one      |
 * |          | position per `--t8`                                                |
 *
 * **There is no spinner anywhere in this app** (§12.7) and nothing in JavaScript
 * drives the loader: the advance is one CSS animation over `steps()`, so it costs no
 * main-thread work on a mid Android and it degrades correctly under
 * `prefers-reduced-motion` — `motion.css` collapses every animation to a single 1ms
 * iteration, which leaves the head lit on the first tick instead of travelling
 * (§7.4, §8.8).
 *
 * The fifth job of §6.6 — the pull-to-refresh indicator — is the same `loader`
 * variant driven by the gesture owner, which advances it by writing the tick count.
 *
 * **The ruler is never a waveform.** When a track has no server peaks the designed
 * state is this ruler plus a 3px `--accent` progress bar and nothing else; a waveform
 * synthesised from a hash is banned outright (§6.7, §12.6) and no code here could
 * produce one — a `Ruler` has no amplitude input at all.
 */

import type { CSSProperties, JSX } from 'react';

import styles from './primitives.module.css';
import { cx } from './Rule';

export type RulerVariant = 'scale' | 'glyph' | 'loader' | 'nav';

/** The bullet and the nav mark are exactly three ticks — one bar of the motif. */
const GLYPH_TICKS = 3;

/** Defaults per variant. `scale` is two bars of twelve; the loader is one. */
const DEFAULT_TICKS: Readonly<Record<RulerVariant, number>> = {
  scale: 24,
  glyph: GLYPH_TICKS,
  loader: 12,
  nav: GLYPH_TICKS,
};

export interface RulerProps {
  /** Which of §6.6's jobs this ruler is doing. Default `scale`. */
  readonly variant?: RulerVariant;
  /**
   * Tick count. Rounded down to a whole number of three-tick bars, so the last tick
   * is never a half-finished bar, and clamped to at least one bar. Ignored by
   * `glyph` and `nav`, which are exactly three ticks by definition.
   */
  readonly count?: number;
  /**
   * `loader` only: what is being read, for a screen reader. Rendered as
   * screen-reader-only text inside the live region — the visible status line is the
   * screen's own mono `--fs-13` copy (§8.8).
   *
   * There is no progress number here on purpose: if the API returns real progress a
   * screen shows `042 / 128`, and if it does not, it shows nothing numeric. No
   * fabricated counters, not once (§8.8, §12.6).
   */
  readonly label?: string | undefined;
  readonly className?: string | undefined;
}

/** Round a requested tick count down to whole three-tick bars, minimum one bar. */
export function barTicks(requested: number): number {
  if (!Number.isFinite(requested)) return GLYPH_TICKS;
  const whole = Math.floor(requested / GLYPH_TICKS) * GLYPH_TICKS;
  return whole < GLYPH_TICKS ? GLYPH_TICKS : whole;
}

export function Ruler({ variant = 'scale', count, label, className }: RulerProps): JSX.Element {
  const fixed = variant === 'glyph' || variant === 'nav';
  const ticks = fixed ? GLYPH_TICKS : barTicks(count ?? DEFAULT_TICKS[variant]);
  const isLoader = variant === 'loader';

  /*
   * The loader's step count drives both the animation's `steps()` and the distance
   * the head travels, so it is one custom property rather than two magic numbers.
   * A CSS custom property is not part of `CSSProperties`, hence the assertion — the
   * value itself is a plain string.
   */
  const style = isLoader ? ({ '--ruler-steps': String(ticks) } as CSSProperties) : undefined;

  const bars = (
    <span className={cx('ruler', (variant === 'glyph' || variant === 'nav') && 'ruler--glyph')}>
      {Array.from({ length: ticks }, (_unused, index) => (
        <i
          key={index}
          className={cx(!isLoader && (index + 1) % GLYPH_TICKS === 0 && styles.drop)}
          data-drop={!isLoader && (index + 1) % GLYPH_TICKS === 0 ? 'true' : undefined}
        />
      ))}
    </span>
  );

  if (!isLoader) {
    /* A scale, a bullet and a nav mark are printed marks, not content: they carry no
       information a screen reader needs, and the thing they mark always has its own
       accessible name. */
    return (
      <span
        className={cx(styles.ticks, className)}
        data-variant={variant}
        data-ticks={String(ticks)}
        aria-hidden="true"
      >
        {bars}
      </span>
    );
  }

  return (
    <span
      className={cx(styles.ticks, styles.loader, className)}
      data-variant="loader"
      data-ticks={String(ticks)}
      style={style}
      role="status"
    >
      {label === undefined || label === '' ? null : <span className={styles.sr}>{label}</span>}
      <span aria-hidden="true">{bars}</span>
      <span className={styles.head} aria-hidden="true" />
    </span>
  );
}
